import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import { useLogin } from "../contexts/LoginContext";
import { signInWithGoogle, signUpWithEmail, signInWithEmail } from "../firebase/auth";
import { createUserProfile, updateUserLastLogin, getUserProfile, addNewsletterSignup, isUserSubscribed } from "../firebase/firestore";

const logo = "/assets/logo1.png";

/* Icons (inline, no deps) */
const IconClose = (p) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <path d="M6 6l12 12M18 6l-12 12" />
  </svg>
);
const IconGoogle = (p) => (
  <svg viewBox="0 0 533.5 544.3" width="18" height="18" aria-hidden="true" {...p}>
    <path fill="#4285f4" d="M533.5 278.4c0-18.6-1.6-37-5-54.8H272v103.8h147.1c-6.3 34-25.2 62.8-53.9 82v67h87.2c51.1-47.1 80.1-116.6 80.1-198z"/>
    <path fill="#34a853" d="M272 544.3c72.6 0 133.6-24 178.2-65.1l-87.2-67c-24.2 16.3-55.1 26-90.9 26-69.8 0-128.9-47.1-150.1-110.4H31.9v69.4c44.2 87.8 135.2 147.1 240.1 147.1z"/>
    <path fill="#fbbc04" d="M121.9 327.8c-10.2-30.6-10.2-63.6 0-94.2v-69.4H31.9c-40.3 79.9-40.3 172.9 0 252.9l90-69.3z"/>
    <path fill="#ea4335" d="M272 106.7c37.5-.6 73.7 13.6 101.1 39.7l75.5-75.5C407.4 25.3 342.1 0 272 0 167.1 0 76.1 59.3 31.9 147.1l90 69.4C143.1 153.2 202.2 106.7 272 106.7z"/>
  </svg>
);

/* Use Firebase Auth only - no localStorage needed */

/* Helpers */
const toTitle = (s = "") =>
  s.trim().replace(/\s+/g, " ").toLowerCase().replace(/(^|\s)\p{L}/gu, (m) => m.toUpperCase());

/* Validation rules */
const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const PHONE_RE = /^\+?\d{10,15}$/;

export default function Login() {
  const { openAuth, setOpenAuth, authMode, setAuthMode, pendingDest, setPendingDest } = useLogin();
  const navigate = useNavigate();
  const firstFieldRef = useRef(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  const [toast, setToast] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const handleAuthSuccess = (userData) => {
    setUser(userData);
    // User data is stored in Firestore, no localStorage needed
  };

  useEffect(() => {
    if (openAuth && firstFieldRef.current) firstFieldRef.current.focus();
  }, [openAuth, authMode]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpenAuth(false); };
    if (openAuth) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openAuth, setOpenAuth]);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const typedName = toTitle((fd.get("name") || "").toString());
    const typedEmail = (fd.get("email") || "").toString().trim().toLowerCase();
    const typedPhone = (fd.get("phone") || "").toString().trim();
    const password = (fd.get("password") || "").toString();

    if (!typedEmail || !PASSWORD_RE.test(password)) {
      setToast({ msg: "Please enter a valid email and password (min 8 chars with uppercase, lowercase, number, and symbol)", type: "error" });
      return;
    }

    try {
      if (authMode === "signup") {
        if (!typedName || !PHONE_RE.test(typedPhone)) {
          setToast({ msg: "Please enter a valid name and phone number", type: "error" });
          return;
        }
        
        // Use Firebase Auth for signup
        const user = await signUpWithEmail(typedEmail, password, typedName);
        
        // Create user profile in Firestore
        try {
          await createUserProfile({
            uid: user.uid,
            name: typedName,
            email: typedEmail,
            phone: typedPhone,
            displayName: typedName
          });} catch (profileError) {
          console.error("❌ Failed to create user profile in Firestore:", profileError);
          // Continue anyway - user is still authenticated
        }
        
        // User is authenticated via Firebase Auth
        const nextUser = { name: typedName, email: typedEmail, phone: typedPhone, uid: user.uid };
        setUser(nextUser);
        setToast({ msg: `Welcome, ${typedName}!`, type: "success" });
        
        // Add user to newsletter automatically on signup - ALWAYS save to newsletter collection
        try {
          const alreadySubscribed = await isUserSubscribed(typedEmail);
          
          if (!alreadySubscribed) {
            // Clean phone number (remove country code if present)
            let cleanPhone = typedPhone ? typedPhone.replace(/^\+?91/, '').trim() : '';
            if (!cleanPhone) cleanPhone = '0000000000'; // Placeholder if no phone
            
            // Determine country code and full phone
            let countryCode = "+91";
            let fullPhone = `+91${cleanPhone}`;
            
            if (typedPhone && typedPhone.startsWith("+")) {
              // Extract country code from phone number
              const match = typedPhone.match(/^(\+\d{1,3})/);
              if (match) {
                countryCode = match[1];
                fullPhone = typedPhone;
              }
            }
            
            const newsletterData = {
              email: typedEmail,
              whatsapp: cleanPhone,
              countryCode: countryCode,
              fullPhone: fullPhone,
              source: "signup",
              userId: user.uid,
              name: typedName,
            };
            
            await addNewsletterSignup(newsletterData);
          }
        } catch (newsletterError) {
          // Don't block signup if newsletter fails, but log for debugging
          console.error("Newsletter signup failed during signup:", newsletterError);
        }
        
        // Dispatch custom event to notify other components of user login
        window.dispatchEvent(new CustomEvent('userLogin', { detail: nextUser }));
      } else {
        // Use Firebase Auth for signin
        const user = await signInWithEmail(typedEmail, password);
        
        // Update last login time in Firestore and ensure user exists
        try {
          // First, try to update last login (this will fail if user doesn't exist in Firestore)
          await updateUserLastLogin(user.uid);} catch (loginError) {// If user doesn't exist in Firestore, create their profile
          try {
            await createUserProfile({
              uid: user.uid,
              name: user.displayName || 'User',
              email: user.email,
              phone: '',
              displayName: user.displayName || 'User'
            });} catch (createError) {
            console.error("❌ Failed to create user profile in Firestore:", createError);
            // Continue anyway - user is still authenticated
          }
        }
        
        // Get user profile from Firestore
        const userProfile = await getUserProfile(user.uid);
        const nameToKeep = userProfile?.name || userProfile?.displayName || user.displayName || typedName || "Guest";
        const userPhone = userProfile?.phoneNumber || userProfile?.phone || "";
        const nextUser = { name: nameToKeep, email: typedEmail, phone: userPhone, uid: user.uid };
        setUser(nextUser);
        setToast({ msg: `Welcome back, ${nextUser.name}!`, type: "success" });
        
        // Add existing user to newsletter if not already subscribed - ALWAYS save to newsletter collection
        try {
          const alreadySubscribed = await isUserSubscribed(typedEmail);
          
          if (!alreadySubscribed) {
            // Clean phone number (remove country code if present)
            let cleanPhone = userPhone ? userPhone.replace(/^\+?91/, '').trim() : '';
            if (!cleanPhone) cleanPhone = '0000000000'; // Placeholder if no phone
            
            // Determine country code and full phone
            let countryCode = "+91";
            let fullPhone = `+91${cleanPhone}`;
            
            if (userPhone && userPhone.startsWith("+")) {
              // Extract country code from phone number
              const match = userPhone.match(/^(\+\d{1,3})/);
              if (match) {
                countryCode = match[1];
                fullPhone = userPhone;
              }
            }
            
            const newsletterData = {
              email: typedEmail,
              whatsapp: cleanPhone,
              countryCode: countryCode,
              fullPhone: fullPhone,
              source: "login",
              userId: user.uid,
              name: nameToKeep,
            };
            
            await addNewsletterSignup(newsletterData);
          }
        } catch (newsletterError) {
          // Don't block login if newsletter fails, but log for debugging
          console.error("Newsletter signup failed during login:", newsletterError);
        }
        
        // Dispatch custom event to notify other components of user login
        window.dispatchEvent(new CustomEvent('userLogin', { detail: nextUser }));
      }

      setOpenAuth(false);
      const to = pendingDest || "/";
      setPendingDest(null);
      navigate(to, { replace: true });

      window.clearTimeout(handleAuthSubmit.__t);
      handleAuthSubmit.__t = window.setTimeout(() => setToast(null), 3000);
    } catch (error) {
      console.error("Auth error:", error);
      let errorMessage = "Authentication failed. Please try again.";
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = "No account found with this email. Please sign up first.";
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = "Incorrect password. Please try again.";
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = "An account with this email already exists. Please sign in instead.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Password is too weak. Please use at least 8 characters with uppercase, lowercase, number, and symbol.";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Invalid email address. Please check and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setToast({ msg: errorMessage, type: "error" });
    }
  };

  // Real Google sign-in with Firebase
  const handleGoogle = async () => {
    try {
      setToast({ msg: "Signing in with Google...", type: "info" });
      const user = await signInWithGoogle();
      
      // Extract user info from Firebase user
      const userData = {
        name: user.displayName || "Google User",
        email: user.email || "",
        phone: user.phoneNumber || "",
        uid: user.uid
      };
      
      // User data is stored in Firestore, no localStorage needed
      setUser(userData);
      setOpenAuth(false);
      setToast({ msg: `Welcome, ${userData.name}!`, type: "success" });
      
      // Add Google sign-in user to newsletter if not already subscribed - ALWAYS save to newsletter collection
      try {
        const alreadySubscribed = await isUserSubscribed(userData.email);
        
        if (!alreadySubscribed) {
          // Clean phone number (remove country code if present)
          let cleanPhone = userData.phone ? userData.phone.replace(/^\+?91/, '').trim() : '';
          if (!cleanPhone) cleanPhone = '0000000000'; // Placeholder if no phone
          
          // Determine country code and full phone
          let countryCode = "+91";
          let fullPhone = `+91${cleanPhone}`;
          
          if (userData.phone && userData.phone.startsWith("+")) {
            // Extract country code from phone number
            const match = userData.phone.match(/^(\+\d{1,3})/);
            if (match) {
              countryCode = match[1];
              fullPhone = userData.phone;
            }
          }
          
          const newsletterData = {
            email: userData.email,
            whatsapp: cleanPhone,
            countryCode: countryCode,
            fullPhone: fullPhone,
            source: "google-login",
            userId: user.uid,
            name: userData.name,
          };
          
          await addNewsletterSignup(newsletterData);
        }
      } catch (newsletterError) {
        // Don't block login if newsletter fails, but log for debugging
        console.error("Newsletter signup failed during Google login:", newsletterError);
      }
      
      // Dispatch custom event to notify other components of user login
      window.dispatchEvent(new CustomEvent('userLogin', { detail: userData }));
      
      const to = pendingDest || "/";
      setPendingDest(null);
      navigate(to, { replace: true });
      
      window.clearTimeout(handleGoogle.__t);
      handleGoogle.__t = window.setTimeout(() => setToast(null), 3000);
    } catch (error) {
      console.error("Google sign-in error:", error);
      setToast({ 
        msg: error.message || "Failed to sign in with Google. Please try again.", 
        type: "error" 
      });
      window.clearTimeout(handleGoogle.__t);
      handleGoogle.__t = window.setTimeout(() => setToast(null), 5000);
    }
  };

  if (!openAuth) return null;

  return (
    <div
      className={`auth-overlay ${openAuth ? "open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Authentication"
      onClick={() => setOpenAuth(false)}
    >
      <div className="auth-card" role="document" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="auth-x"
          aria-label="Close"
          title="Close"
          onClick={() => setOpenAuth(false)}
        >
          <IconClose />
        </button>

        <div className="auth-brand">
          <img src={logo} alt="" className="auth-logo" />
          <h3 className="auth-title">Welcome to Varaha</h3>
          <p className="auth-sub">
            Keep your <strong>Wishlist</strong> and <strong>Cart</strong> in sync on any device.
          </p>
        </div>

        {/* Tabs */}
        <div className="auth-tabs" role="tablist" aria-label="Authentication tabs">
          <button
            role="tab"
            aria-selected={authMode === "signin"}
            className={`auth-tab ${authMode === "signin" ? "active" : ""}`}
            onClick={() => setAuthMode("signin")}
            type="button"
          >
            Sign In
          </button>
          <button
            role="tab"
            aria-selected={authMode === "signup"}
            className={`auth-tab ${authMode === "signup" ? "active" : ""}`}
            onClick={() => setAuthMode("signup")}
            type="button"
          >
            Create Account
          </button>
        </div>

        <form className="auth-form" onSubmit={handleAuthSubmit} noValidate>
          {authMode === "signup" ? (
            <>
              <label className="auth-label" htmlFor="name">Full Name</label>
              <input
                id="name"
                name="name"
                type="text"
                className="auth-input"
                placeholder="Enter your full name"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                ref={firstFieldRef}
              />

              <label className="auth-label" htmlFor="email">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                className="auth-input"
                placeholder="your.email@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />

              <label className="auth-label" htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                className="auth-input"
                placeholder="+91XXXXXXXXXX"
                required
                pattern={PHONE_RE.source}
        
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
             

              <label className="auth-label" htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                className="auth-input"
                placeholder="Min 8 chars incl. Aa, 0-9 & symbol"
                required
                minLength={8}
                pattern={PASSWORD_RE.source}
               
                autoComplete="new-password"
              />
            
            </>
          ) : (
            <>
              <label className="auth-label" htmlFor="email">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                className="auth-input"
                placeholder="your.email@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                ref={firstFieldRef}
              />

              <label className="auth-label" htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                className="auth-input"
                placeholder="Your password"
                required
                minLength={8}
                pattern={PASSWORD_RE.source}
              
                autoComplete="current-password"
              />
         
            </>
          )}

          <button type="submit" className="btn-primary">
            {authMode === "signup" ? "Create account" : "Sign in"}
          </button>

          <button
            type="button"
            className="btn-google"
            onClick={handleGoogle}
            aria-label="Continue with Google"
          >
            <IconGoogle />
            <span>Continue with Google</span>
          </button>

          <button
            type="button"
            className="btn-switch"
            onClick={() => setAuthMode(authMode === "signup" ? "signin" : "signup")}
          >
            {authMode === "signup" ? "Have an account? Sign in" : "New here? Create account"}
          </button>
        </form>
      </div>

      {toast && (
        <div className={`toast ${toast.type}`} role="status" aria-live="polite">
          <span className="toast-msg">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}
